import { useState, useRef, useEffect } from 'react';

/**
 * Generic editable-text-field state for the User Profile page (STD-014 split
 * out of UserProfilePage.tsx).
 *
 * Owns the `isEditing` flag plus the working value, and seeds that value from
 * the latest server data whenever it changes — but only while the user is NOT
 * editing the field, so unsaved keystrokes are preserved across background
 * refetches. A ref mirrors `isEditing` so the seeding effect can read the
 * current edit state without becoming a dependency (which would re-run it on
 * every toggle).
 */
export interface EditableField {
  isEditing: boolean;
  value: string;
  setValue: (value: string) => void;
  startEditing: () => void;
  /** Stop editing and reset the value back to the supplied seed. */
  cancelEditing: () => void;
  /** Stop editing while keeping the current value (e.g. after a save). */
  stopEditing: () => void;
}

export function useEditableField(seed: string | null | undefined): EditableField {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const isEditingRef = useRef(false);

  // Keep the ref in sync so the seeding effect below can read the current
  // editing state without becoming a dependency.
  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  // Seed the working value whenever fresh data arrives, but only when the user
  // is not currently editing this field (preserves unsaved changes). Stays
  // inert until data arrives (seed != null), matching the original profile guard.
  useEffect(() => {
    if (seed == null) return;
    if (!isEditingRef.current) setValue(seed ?? '');
  }, [seed]);

  const startEditing = () => setIsEditing(true);

  const cancelEditing = () => {
    setIsEditing(false);
    setValue(seed || '');
  };

  const stopEditing = () => setIsEditing(false);

  return { isEditing, value, setValue, startEditing, cancelEditing, stopEditing };
}
